/**
 * Internal-Transfer Pairing.
 *
 * ANZ M-BANKING transfers between your own accounts carry the same
 * 6-digit reference number on both sides:
 *
 *   Business: "-199.00  ANZ M-BANKING FUNDS TFER TRANSFER 070429  TO  015237280730859"
 *   Everyday: "+199.00  ANZ M-BANKING FUNDS TFER TRANSFER 070429  FROM       300571075"
 *
 * That's the natural pairing key. After every import we look at all
 * unpaired transactions (across all accounts) and try to find their
 * sibling.
 *
 * Why we care: cash-flow totals must EXCLUDE internal transfers, or
 * moving $199 between your own accounts shows as $199 spent AND $199
 * earned on the dashboard. We tag both sides `category: internal_transfer`
 * and the summary code filters on that string.
 */

import { BankTransaction } from '../types';

const TRANSFER_REF_RE = /M-?BANKING\s+FUNDS\s+TFER\s+TRANSFER\s+(\d{4,8})/i;

/** Extract the M-BANKING reference number from a description, or null
 *  if this isn't an internal transfer line at all. */
export function extractTransferRef(description: string): string | null {
  const m = description.match(TRANSFER_REF_RE);
  return m ? m[1] : null;
}

/** Difference in days between two YYYY-MM-DD strings. Positive when a
 *  is later than b. We allow ±2 days slack since the two sides
 *  occasionally appear on slightly different posting dates. */
function dayDiff(a: string, b: string): number {
  const da = new Date(a + 'T00:00:00Z').getTime();
  const db = new Date(b + 'T00:00:00Z').getTime();
  return Math.round((da - db) / 86400000);
}

/** Result of a pairing pass — mutations to apply by the caller. We
 *  don't mutate the input array so this stays pure and testable. */
export interface PairingUpdate {
  id: string;
  category: string;
  transferGroupId: string;
  pairedTransactionId: string;
}

/** Find pairs amongst a flat list of transactions and return the
 *  updates to apply. Pass in EVERY un-paired transaction you've got
 *  (across all accounts) and the function will find matches.
 *
 *  Matching rule:
 *    - Same M-BANKING ref
 *    - Opposite sign
 *    - Equal abs(amount)
 *    - Different accountId
 *    - Date within ±2 days
 *    - Neither side already paired
 *
 *  When more than two candidates carry the same ref (rare — banks
 *  recycle refs over long horizons), pairs are formed greedily by
 *  closest date.
 */
export function findTransferPairs(transactions: BankTransaction[]): PairingUpdate[] {
  // Index by ref. Only transactions that look like internal transfers
  // and aren't already paired are candidates.
  const byRef = new Map<string, BankTransaction[]>();
  for (const tx of transactions) {
    if (tx.pairedTransactionId) continue;
    const ref = extractTransferRef(tx.descriptionRaw);
    if (!ref) continue;
    const arr = byRef.get(ref) || [];
    arr.push(tx);
    byRef.set(ref, arr);
  }

  const updates: PairingUpdate[] = [];
  const claimed = new Set<string>();

  // Array.from to avoid TS downlevelIteration requirement.
  const entries = Array.from(byRef.entries());
  for (let e = 0; e < entries.length; e++) {
    const ref = entries[e][0];
    const candidates = entries[e][1];
    if (candidates.length < 2) continue;

    // Greedy pair-up: for each unclaimed candidate, find its closest-by-date
    // valid partner amongst the remaining unclaimed.
    for (let i = 0; i < candidates.length; i++) {
      const a = candidates[i];
      if (claimed.has(a.id)) continue;

      let bestJ = -1;
      let bestDist = Infinity;
      for (let j = i + 1; j < candidates.length; j++) {
        const b = candidates[j];
        if (claimed.has(b.id)) continue;
        if (a.accountId === b.accountId) continue;
        if (Math.sign(a.amount) === Math.sign(b.amount)) continue;
        if (Math.abs(a.amount) !== Math.abs(b.amount)) continue;
        const dist = Math.abs(dayDiff(a.date, b.date));
        if (dist > 2) continue;
        if (dist < bestDist) {
          bestDist = dist;
          bestJ = j;
        }
      }

      if (bestJ === -1) continue;
      const b = candidates[bestJ];
      claimed.add(a.id);
      claimed.add(b.id);
      const groupId = `tfr_${ref}_${a.date}_${Math.abs(a.amount).toFixed(2)}`;
      updates.push({
        id: a.id,
        category: 'internal_transfer',
        transferGroupId: groupId,
        pairedTransactionId: b.id,
      });
      updates.push({
        id: b.id,
        category: 'internal_transfer',
        transferGroupId: groupId,
        pairedTransactionId: a.id,
      });
    }
  }

  return updates;
}
